import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Amplify } from 'aws-amplify';
import {
  autoSignIn,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  resendSignUpCode,
  signIn,
  signOut as amplifySignOut,
  signUp,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

/*
 * Two modes, picked at build time:
 * - cognito: VITE_COGNITO_USER_POOL_ID + VITE_COGNITO_CLIENT_ID are set.
 *   Passwordless only: a 6-digit email code (sign-up and sign-in share one
 *   flow) or a passkey. Requests carry the Cognito ID token.
 * - dev: neither is set. Any email signs in and is sent as `x-dev-user`,
 *   which the API accepts only with AUTH_MODE=dev.
 */

const poolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
export const AUTH_MODE: 'cognito' | 'dev' = poolId && clientId ? 'cognito' : 'dev';

if (AUTH_MODE === 'cognito') {
  Amplify.configure({
    Auth: {
      Cognito: { userPoolId: poolId!, userPoolClientId: clientId!, loginWith: { email: true } },
    },
  });
}

const DEV_STORAGE_KEY = 'ready.devUser';

export interface SessionUser {
  email: string;
}

/** Result of starting an email sign-in: a code is on its way, or we're already in. */
export type EmailStart = 'code_sent' | 'signed_in';

interface AuthContextValue {
  mode: typeof AUTH_MODE;
  user: SessionUser | null;
  loading: boolean;
  startEmailSignIn: (email: string) => Promise<EmailStart>;
  confirmCode: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  signInWithPasskey: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  authHeaders: () => Promise<Record<string, string>>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readDevUser(): SessionUser | null {
  try {
    const email = localStorage.getItem(DEV_STORAGE_KEY);
    return email ? { email } : null;
  } catch {
    return null;
  }
}

async function cognitoSessionUser(): Promise<SessionUser | null> {
  try {
    const { tokens } = await fetchAuthSession();
    const email = tokens?.idToken?.payload.email;
    return typeof email === 'string' ? { email } : null;
  } catch {
    return null;
  }
}

const errorName = (err: unknown) => (err as { name?: string })?.name;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(AUTH_MODE === 'dev' ? readDevUser() : null);
  const [loading, setLoading] = useState(AUTH_MODE === 'cognito');
  // The pending code is either for confirming a new sign-up or for an email-OTP sign-in.
  const pending = useRef<{ kind: 'signup' | 'signin'; email: string } | null>(null);

  const refresh = useCallback(async () => {
    setUser(await cognitoSessionUser());
    setLoading(false);
  }, []);

  useEffect(() => {
    if (AUTH_MODE !== 'cognito') return;
    void refresh();
    return Hub.listen('auth', ({ payload }) => {
      if (
        payload.event === 'signedIn' ||
        payload.event === 'signedOut' ||
        payload.event === 'tokenRefresh'
      ) {
        void refresh();
      }
    });
  }, [refresh]);

  const startEmailSignIn = useCallback(async (rawEmail: string): Promise<EmailStart> => {
    const email = rawEmail.trim().toLowerCase();
    if (AUTH_MODE === 'dev') {
      try {
        localStorage.setItem(DEV_STORAGE_KEY, email);
      } catch {
        // Storage unavailable; the session lasts until reload.
      }
      setUser({ email });
      return 'signed_in';
    }

    // New people sign up (Cognito emails a confirmation code); existing people
    // get an email one-time code. Either way the next screen asks for a code.
    try {
      await signUp({
        username: email,
        options: { userAttributes: { email }, autoSignIn: { authFlowType: 'USER_AUTH' } },
      });
      pending.current = { kind: 'signup', email };
      return 'code_sent';
    } catch (err) {
      if (errorName(err) !== 'UsernameExistsException') throw err;
    }

    try {
      const { nextStep } = await signIn({
        username: email,
        options: { authFlowType: 'USER_AUTH', preferredChallenge: 'EMAIL_OTP' },
      });
      if (nextStep.signInStep === 'DONE') return 'signed_in';
      pending.current = { kind: 'signin', email };
      return 'code_sent';
    } catch (err) {
      // Signed up earlier but never entered the code: send a fresh one.
      if (errorName(err) !== 'UserNotConfirmedException') throw err;
      await resendSignUpCode({ username: email });
      pending.current = { kind: 'signup', email };
      return 'code_sent';
    }
  }, []);

  const confirmCode = useCallback(async (code: string) => {
    const p = pending.current;
    if (!p) throw new Error('Start again: no sign-in in progress');
    if (p.kind === 'signup') {
      const { nextStep } = await confirmSignUp({
        username: p.email,
        confirmationCode: code.trim(),
      });
      if (nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') await autoSignIn();
    } else {
      await confirmSignIn({ challengeResponse: code.trim() });
    }
    pending.current = null;
  }, []);

  const resendCode = useCallback(async () => {
    const p = pending.current;
    if (!p) return;
    if (p.kind === 'signup') {
      await resendSignUpCode({ username: p.email });
    } else {
      await signIn({
        username: p.email,
        options: { authFlowType: 'USER_AUTH', preferredChallenge: 'EMAIL_OTP' },
      });
    }
  }, []);

  const signInWithPasskey = useCallback(async (email: string) => {
    await signIn({
      username: email.trim().toLowerCase(),
      options: { authFlowType: 'USER_AUTH', preferredChallenge: 'WEB_AUTHN' },
    });
  }, []);

  const signOut = useCallback(async () => {
    if (AUTH_MODE === 'cognito') {
      await amplifySignOut();
    } else {
      try {
        localStorage.removeItem(DEV_STORAGE_KEY);
      } catch {
        // ignore
      }
    }
    setUser(null);
  }, []);

  const authHeaders = useCallback(async (): Promise<Record<string, string>> => {
    if (AUTH_MODE === 'dev') return user ? { 'x-dev-user': user.email } : {};
    const { tokens } = await fetchAuthSession();
    const idToken = tokens?.idToken?.toString();
    return idToken ? { authorization: `Bearer ${idToken}` } : {};
  }, [user]);

  const value = useMemo(
    () => ({
      mode: AUTH_MODE,
      user,
      loading,
      startEmailSignIn,
      confirmCode,
      resendCode,
      signInWithPasskey,
      signOut,
      authHeaders,
    }),
    [
      user,
      loading,
      startEmailSignIn,
      confirmCode,
      resendCode,
      signInWithPasskey,
      signOut,
      authHeaders,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
